import { create } from 'zustand';
import type { Notification, IssuanceProject } from '../types';

interface AppState {
  // UI状态
  sidebarOpen: boolean;
  currentStep: number;
  notifications: Notification[];

  // 项目状态
  currentProject: IssuanceProject | null;
  projects: IssuanceProject[];

  // 加载状态
  isLoading: boolean;

  // Actions
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  setCurrentStep: (step: number) => void;
  nextStep: () => void;
  prevStep: () => void;

  // 通知管理
  addNotification: (
    notification: Omit<Notification, 'id' | 'timestamp'>
  ) => void;
  removeNotification: (id: string) => void;
  markNotificationRead: (id: string) => void;
  clearNotifications: () => void;

  // 项目管理
  setCurrentProject: (project: IssuanceProject | null) => void;
  setProjects: (projects: IssuanceProject[]) => void;
  addProject: (project: IssuanceProject) => void;
  updateProject: (id: string, updates: Partial<IssuanceProject>) => void;
  removeProject: (id: string) => void;

  // 全局加载状态
  setLoading: (loading: boolean) => void;
}

export const useAppStore = create<AppState>((set, get) => ({
  // 初始状态
  sidebarOpen: true,
  currentStep: 0,
  notifications: [],
  currentProject: null,
  projects: [],
  isLoading: false,

  // UI Actions
  toggleSidebar: () => {
    set(state => ({ sidebarOpen: !state.sidebarOpen }));
  },

  setSidebarOpen: (open: boolean) => {
    set({ sidebarOpen: open });
  },

  setCurrentStep: (step: number) => {
    set({ currentStep: step });
  },

  nextStep: () => {
    set(state => ({ currentStep: state.currentStep + 1 }));
  },

  prevStep: () => {
    set(state => ({
      currentStep: Math.max(0, state.currentStep - 1),
    }));
  },

  // 通知管理
  addNotification: notification => {
    const newNotification: Notification = {
      ...notification,
      id: Date.now().toString(),
      timestamp: new Date(),
      read: false,
    };

    set(state => ({
      notifications: [newNotification, ...state.notifications],
    }));

    // 自动移除成功通知
    if (notification.type === 'success') {
      setTimeout(() => {
        get().removeNotification(newNotification.id);
      }, 5000);
    }
  },

  removeNotification: (id: string) => {
    set(state => ({
      notifications: state.notifications.filter(n => n.id !== id),
    }));
  },

  markNotificationRead: (id: string) => {
    set(state => ({
      notifications: state.notifications.map(n =>
        n.id === id ? { ...n, read: true } : n
      ),
    }));
  },

  clearNotifications: () => {
    set({ notifications: [] });
  },

  // 项目管理
  setCurrentProject: (project: IssuanceProject | null) => {
    set({ currentProject: project });
  },

  setProjects: (projects: IssuanceProject[]) => {
    set({ projects });
  },

  addProject: (project: IssuanceProject) => {
    set(state => ({
      projects: [...state.projects, project],
    }));
  },

  updateProject: (id: string, updates: Partial<IssuanceProject>) => {
    set(state => ({
      projects: state.projects.map(p =>
        p.id === id ? { ...p, ...updates } : p
      ),
      currentProject:
        state.currentProject?.id === id
          ? { ...state.currentProject, ...updates }
          : state.currentProject,
    }));
  },

  removeProject: (id: string) => {
    set(state => ({
      projects: state.projects.filter(p => p.id !== id),
      currentProject:
        state.currentProject?.id === id ? null : state.currentProject,
    }));
  },

  // 全局加载状态
  setLoading: (loading: boolean) => {
    set({ isLoading: loading });
  },
}));
